<template>
  <div class="postback-logs">
    <div class="postback-logs__search form-item pb-0">
      <div class="form-item__field mb-0">
        <div class="search-field">
          <input v-model.trim="search" type="search" :placeholder="$t('platforms.search_by_link')" @keydown.enter="load" />
          <button type="button" class="icon-search" :title="$t('platforms.search_by_link')" @click="load">
            <svg v-svg symbol="search" size="0 0 15 16"></svg>
          </button>
        </div>
      </div>
    </div>
    <div class="c-table mt-3">
      <div class="inner">
        <vue-good-table styleClass="table  _no-wrap" :columns="columns" :rows="pageRows">
          <template slot="table-column" slot-scope="props">
            <span>{{ props.column.label }}:</span>
          </template>
          <template slot="table-row" slot-scope="props">
            <template v-if="props.column.field === 'id'">
              {{ props.row.id }}
            </template>
            <template v-else-if="props.column.field === 'date'">{{ props.row.request_sent_at }}</template>
            <template v-else-if="props.column.field === 'link'">
              <span class="postback-url">
                <span class="icon-link">
                  <svg v-svg symbol="link" size="0 0 17 17"></svg>
                </span>
                {{ props.row.url }}
              </span>
            </template>
            <template v-else-if="props.column.field === 'status'">
              <span
                v-tooltip="{
                  content: details(props.row),
                  offset: '10',
                  html: true,
                  triggers: ['hover', 'click'],
                }"
                class="postback-status"
                :class="props.row.status_code == 200 ? 'success' : 'error'"
              >
                <template v-if="props.row.status_code == 200">
                  <svg width="9" height="7" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3.22136 7C3.04127 7 2.87019 6.92187 2.74412 6.78514L0.195848 4.02128C-0.0652826 3.73805 -0.0652826 3.26927 0.195848 2.98605C0.456978 2.70283 0.889194 2.70283 1.15033 2.98605L3.22136 5.2323L7.84968 0.212417C8.11081 -0.0708057 8.54302 -0.0708057 8.80415 0.212417C9.06528 0.49564 9.06528 0.964422 8.80415 1.24765L3.6986 6.78514C3.57254 6.92187 3.40145 7 3.22136 7Z" />
                  </svg>
                </template>
                <template v-else>
                  <span> ! </span>
                </template>
              </span>
            </template>
          </template>
          <div class="d-flex justify-content-center" slot="emptystate">
            <template v-if="!loading">
              {{ $t("any_results") }}
            </template>
            <img v-else :src="require('@/assets/images/rolling.svg')" alt="loader" class="loader" />
          </div>
        </vue-good-table>
      </div>
    </div>
    <div class="c-pagination">
      <ul class="pagination" v-if="rows.length > 0">
        <li class="page-item">
          <button @click.prevent="page--" :class="{ disabled: page <= 1 }" :disabled="page <= 1" class="page-link icon-prev prev" :title="$t('statistic.prev')">
            <svg v-svg symbol="prev" size="0 0 8 14"></svg>
          </button>
        </li>
        <li class="page-item">
          <button @click.prevent="page++" :class="{ disabled: page * limit >= rows.length }" :disabled="page * limit >= rows.length" class="page-link icon-next next" :title="$t('statistic.next')">
            <svg v-svg symbol="next" size="0 0 8 14"></svg>
          </button>
        </li>
        <li class="page-item">{{ limit * (page - 1) + 1 }} - {{ limit * (page - 1) + pageRows.length }} {{ $t("from") }} {{ rows.length }}</li>
        <li>
          <custom-multiselect class="ml-2" v-model="limit" :list="limits" :allowEmpty="false">
            <template v-slot:block="props">
              <span>{{ props.value }}</span>
            </template>
            <template v-slot:list="props">
              <span>{{ props.value }}</span>
            </template>
          </custom-multiselect>
        </li>
      </ul>
    </div>
  </div>
</template>
<script>
import servicePostback from "@/api/servicePostback";
import "@/assets/css/_pagination.scss";

// Postbacks sent for one platform (`platformId`) or for all of them. The API returns the whole log at once, the
// pages are cut here.
export default {
  name: "PostbackLogs",
  props: {
    platformId: {
      type: [Number, String],
      default: null,
    },
  },
  data() {
    return {
      search: "",
      loading: false,
      rows: [],
      page: 1,
      limit: Number(localStorage.getItem("paginationLimit")) || 10,
      limits: [10, 25, 50, 100],
    };
  },
  computed: {
    columns() {
      return [
        { label: this.platformId ? "ID" : "Source ID", field: "id", sortable: false },
        { label: this.$t("platforms.postback.table.date"), field: "date", sortable: false },
        { label: this.$t("platforms.postback.table.link"), field: "link", sortable: false },
        { label: this.$t("platforms.postback.table.status"), field: "status", sortable: false },
      ];
    },
    pageRows() {
      return this.rows.slice(this.limit * (this.page - 1), this.limit * this.page);
    },
    timeValue() {
      return this.$i18n.locale === "ru" || this.$i18n.locale === "uk" ? "мс" : "ms";
    },
  },
  watch: {
    limit() {
      this.page = 1;
      localStorage.setItem("paginationLimit", this.limit);
    },
  },
  created() {
    this.load();
  },
  methods: {
    load() {
      const payload = { url: this.search || undefined };
      const request = this.platformId ? servicePostback.getPlatformsPostback(payload, this.platformId) : servicePostback.getPostback(payload);
      this.loading = true;
      request
        .then((res) => {
          if (res && res.status == 200) {
            this.rows = res.data;
            this.page = 1;
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
    details(data) {
      return `
        <span class='postback-desc'>
          <span class='postback-desc__block'>
            <span class='postback-desc__title'>${this.$t("platforms.postback.table.status")}:</span>  <span class='postback-desc__value ${data.status_code == 200 ? "success" : "error"}'>${data.status_code}</span>
          </span>
          <span class='postback-desc__block'>
            <span class='postback-desc__title'>${this.$t("platforms.postback.response_time")}:</span>  <span class='postback-desc__value'>${data.response_time} ${this.timeValue}</span>
          </span>
          <span class='postback-desc__block'>
            <span class='postback-desc__title'>${this.$t("platforms.postback.description")}:</span>  <span class='postback-desc__value'>${data.description}</span>
          </span>
        </span>
        `;
    },
  },
};
</script>
<style lang="scss">
.postback-url {
  color: #317475;
  svg {
    fill: #317475;
  }
}

.postback-status {
  position: relative;
  width: 21px;
  height: 21px;
  font-size: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid #262627;
  border-radius: 50%;
  transition: 0.25s all ease;
  cursor: pointer;

  svg {
    fill: #262627;
  }

  &.error {
    border-color: #c62424;
    color: #c62424;

    &:focus,
    &:hover {
      background: #c62424;
      color: white;
    }
  }

  &.success {
    border-color: #37807d;
    svg {
      fill: #37807d;
    }
    &:focus,
    &:hover {
      background: #37807d;
      svg {
        fill: white;
      }
    }
  }
}

.postback-desc {
  display: flex;
  flex-direction: column;
  background: white;

  &__block {
    display: flex;
    width: 100%;
    gap: 0 6px;
  }

  &__title {
    font-weight: 400;
    color: #9e9e9e;
  }

  &__value {
    font-weight: 500;
    color: #141414;

    &.error {
      color: #c62424;
    }

    &.success {
      color: #37807d;
    }
  }
}
</style>
<style lang="scss" scoped>
.postback-logs__search {
  max-width: 520px;
}
</style>
